import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate, useParams } from 'react-router-dom';
import { IoCreateOutline, IoEyeOutline } from 'react-icons/io5';
import quoteService from '../../../../services/quoteService';
import { getNodeServiceBaseUrl, getSwaggerServiceBaseUrl } from '../../../../config/apiBase';
import { EmptyState, ErrorState } from '../../../../shared-ui/States';
import '../../../../shared-ui/RowActions.css';

const token = () => localStorage.getItem('zTaskAuthToken') || '';
const authHeaders = () => ({
  Authorization: `Bearer ${token()}`,
  'Content-Type': 'application/json',
  'X-Client': 'web',
});

const blankLine = () => ({
  work_type_id: '',
  description: '',
  uom: '',
  qty: 1,
  rate: 1000,
  cgst_pct: 9,
  sgst_pct: 9,
  discount_pct: 0,
  custom_description: false,
});

const canEditQuote = (status) => status === 'DRAFT' || status === 'REJECTED';

export default function QuotationList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setErr('');
    try {
      const res = await quoteService.list();
      setRows(res.data || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const act = async (fn) => {
    setBusy(true);
    setErr('');
    try {
      await fn();
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0 }}>Quotations</h2>
          <p style={{ color: '#666', marginTop: 4 }}>ZOrder — Quote → Work Order (Wave 1)</p>
        </div>
        <Link
          to="/quotations/new"
          style={{
            background: '#2563eb',
            color: '#fff',
            padding: '10px 16px',
            borderRadius: 8,
            textDecoration: 'none',
          }}
        >
          + New Quote
        </Link>
      </div>

      {!loading && err && (
        <ErrorState
          title={rows.length ? 'Action failed' : 'Could not load quotations'}
          description={err}
          onRetry={rows.length ? undefined : load}
        />
      )}

      {loading ? (
        <p style={{ marginTop: 16, color: '#666' }}>Loading quotations…</p>
      ) : !err && rows.length === 0 ? (
        <EmptyState
          title="No quotations"
          description="Create a quote to start the Quote → Work Order flow."
        />
      ) : rows.length > 0 ? (
      <table style={{ width: '100%', marginTop: 16, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}>
            <th style={{ padding: 8 }}>Quote No</th>
            <th>Customer</th>
            <th>Date</th>
            <th>Status</th>
            <th>Total</th>
            <th style={{ textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((q) => (
            <tr key={q.id} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: 8 }}>
                <Link to={`/quotations/${q.id}`}>{q.quote_no}</Link>
              </td>
              <td>{q.customer_name || q.customer_id || '—'}</td>
              <td>{q.quote_date ? String(q.quote_date).slice(0, 10) : '—'}</td>
              <td>{q.status}</td>
              <td>{q.total_amt}</td>
              <td style={{ padding: 8 }}>
                <div className="crane-row-actions" style={{ flexWrap: 'wrap', justifyContent: 'flex-start' }}>
                  <NavLink
                    to={`/quotations/${q.id}`}
                    className="crane-row-action crane-row-action--view"
                    title="View"
                    aria-label="View quotation"
                  >
                    <IoEyeOutline size={16} />
                  </NavLink>
                  {canEditQuote(q.status) && (
                    <NavLink
                      to={`/quotations/${q.id}/edit`}
                      className="crane-row-action crane-row-action--edit"
                      title="Edit"
                      aria-label="Edit quotation"
                    >
                      <IoCreateOutline size={16} />
                    </NavLink>
                  )}
                  {q.status === 'DRAFT' && (
                    <button
                      type="button"
                      className="crane-row-action crane-row-action--workflow"
                      disabled={busy}
                      onClick={() => act(() => quoteService.submit(q.id))}
                    >
                      Submit
                    </button>
                  )}
                  {q.status === 'REJECTED' && (
                    <button
                      type="button"
                      className="crane-row-action crane-row-action--workflow"
                      disabled={busy}
                      onClick={() => navigate(`/quotations/${q.id}/edit`)}
                    >
                      Fix &amp; resubmit
                    </button>
                  )}
                  {q.status === 'SUBMITTED' && (
                    <>
                      <button
                        type="button"
                        className="crane-row-action crane-row-action--workflow"
                        disabled={busy}
                        onClick={() => act(() => quoteService.approve(q.id))}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="crane-row-action crane-row-action--workflow"
                        disabled={busy}
                        onClick={() => act(() => quoteService.reject(q.id))}
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {q.status === 'APPROVED' && (
                    <button
                      type="button"
                      className="crane-row-action crane-row-action--workflow"
                      disabled={busy}
                      onClick={() => navigate(`/quotations/${q.id}/convert`)}
                    >
                      Complete WO &amp; convert →
                    </button>
                  )}
                  {q.status === 'CONVERTED' && q.converted_wo_id && (
                    <Link to={`/work-orders/${q.converted_wo_id}`} className="crane-row-action crane-row-action--workflow">
                      Open WO
                    </Link>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      ) : null}
    </div>
  );
}

export function QuotationForm() {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const isEdit = Boolean(routeId) && routeId !== 'new';
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [units, setUnits] = useState([]);
  const [invoiceFors, setInvoiceFors] = useState([]);
  const [err, setErr] = useState('');
  const [loadMsg, setLoadMsg] = useState('Loading masters…');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    customer_id: '',
    project_id: '',
    remarks: '',
    valid_until: '',
    lines: [blankLine()],
  });

  const normalizeCustomers = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.customers)) return payload.customers;
    if (Array.isArray(payload?.message)) return payload.message;
    return [];
  };

  const normalizeProjects = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.projects)) return payload.projects;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.message)) return payload.message;
    return [];
  };

  const normalizeList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  };

  const unitLabel = (unitId, unitOpts = units) => {
    if (unitId == null || unitId === '') return '';
    const hit = unitOpts.find((u) => String(u.id) === String(unitId));
    return hit?.value || '';
  };

  const applyWorkTypeToLine = (line, wtId, wts = workTypes, unitOpts = units) => {
    if (!wtId || wtId === '__custom__') {
      return {
        ...line,
        work_type_id: '',
        custom_description: wtId === '__custom__',
        description: wtId === '__custom__' ? line.description : '',
      };
    }
    const wt = wts.find((w) => String(w.id) === String(wtId));
    if (!wt) {
      return { ...line, work_type_id: wtId, custom_description: false };
    }
    return {
      ...line,
      work_type_id: String(wt.id),
      custom_description: false,
      description: wt.name || line.description,
      uom: unitLabel(wt.unit_id, unitOpts) || line.uom || '',
      cgst_pct: wt.cgst != null ? Number(wt.cgst) : line.cgst_pct,
      sgst_pct: wt.sgst != null ? Number(wt.sgst) : line.sgst_pct,
    };
  };

  const loadMasters = async () => {
    setErr('');
    setLoadMsg('Loading customers, projects & line masters…');
    if (!token()) {
      setErr('Not logged in — open ZOrder from the launcher so a token is present.');
      setLoadMsg('');
      return;
    }
    try {
      const node = getNodeServiceBaseUrl();
      const [cRes, pRes, wtRes, unitRes, invForRes] = await Promise.all([
        fetch(`${node}/customer/api/customers/`, { headers: authHeaders() }),
        fetch(`${getSwaggerServiceBaseUrl()}/api/get-project`, { headers: authHeaders() }),
        fetch(`${node}/work-type/api/work-types/`, { headers: authHeaders() }),
        fetch(`${node}/api/get-column/common_master/unit`, { headers: authHeaders() }),
        fetch(`${node}/api/get-column/common_master/invoice_for`, { headers: authHeaders() }),
      ]);
      const cBody = await cRes.json().catch(() => ({}));
      const pBody = await pRes.json().catch(() => ({}));
      const wtBody = await wtRes.json().catch(() => ({}));
      const unitBody = await unitRes.json().catch(() => ({}));
      const invForBody = await invForRes.json().catch(() => ({}));

      if (!cRes.ok) throw new Error(cBody.message || cBody.error?.message || `Customers HTTP ${cRes.status}`);
      if (!pRes.ok) throw new Error(pBody.message || pBody.error?.message || `Projects HTTP ${pRes.status}`);

      const custs = normalizeCustomers(cBody).filter((c) => c && (c.id != null || c.customer_id != null));
      const projs = normalizeProjects(pBody).filter((p) => p && (p.projectId != null || p.id != null));
      const wts = normalizeList(wtBody).filter((w) => w && w.id != null && w.active !== false && w.active !== 0);
      const unitOpts = normalizeList(unitBody);
      const invFors = normalizeList(invForBody);

      setCustomers(custs);
      setProjects(projs);
      setWorkTypes(wts);
      setUnits(unitOpts);
      setInvoiceFors(invFors);

      setForm((f) => {
        const lines = [...(f.lines || [])];
        if (lines.length && !lines[0].description && !lines[0].work_type_id && wts[0]) {
          lines[0] = applyWorkTypeToLine(lines[0], String(wts[0].id), wts, unitOpts);
        } else if (lines.length && !lines[0].description && !wts.length && invFors[0]) {
          lines[0] = { ...lines[0], description: invFors[0].value, work_type_id: '' };
        }
        return { ...f, lines };
      });

      if (!custs.length || !projs.length) {
        setErr(
          `Masters loaded but empty (customers=${custs.length}, projects=${projs.length}). Create masters first.`
        );
      } else if (!wts.length && !invFors.length) {
        setErr('No work types / invoice_for masters found — use Custom description.');
      }
      setLoadMsg('');
    } catch (e) {
      setCustomers([]);
      setProjects([]);
      setWorkTypes([]);
      setUnits([]);
      setInvoiceFors([]);
      setErr(e.message || 'Failed to load masters');
      setLoadMsg('');
    }
  };

  useEffect(() => {
    loadMasters();
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      setLoadMsg('Loading quotation…');
      try {
        const res = await quoteService.get(routeId);
        const q = res.data;
        if (cancelled) return;
        if (!canEditQuote(q.status)) {
          setErr(`Only DRAFT or REJECTED quotes can be edited (current: ${q.status}).`);
          setLoadMsg('');
          return;
        }
        setForm({
          customer_id: q.customer_id != null ? String(q.customer_id) : '',
          project_id: q.project_id != null ? String(q.project_id) : '',
          remarks: q.remarks || '',
          valid_until: q.valid_until ? String(q.valid_until).slice(0, 10) : '',
          lines:
            Array.isArray(q.lines) && q.lines.length
              ? q.lines.map((l) => ({
                  work_type_id: l.work_type_id != null ? String(l.work_type_id) : '',
                  description: l.description || '',
                  uom: l.uom || '',
                  qty: l.qty != null ? Number(l.qty) : 1,
                  rate: l.rate != null ? Number(l.rate) : 0,
                  cgst_pct: l.cgst_pct != null ? Number(l.cgst_pct) : 0,
                  sgst_pct: l.sgst_pct != null ? Number(l.sgst_pct) : 0,
                  discount_pct: l.discount_pct != null ? Number(l.discount_pct) : 0,
                  custom_description: !l.work_type_id,
                }))
              : [blankLine()],
        });
        setLoadMsg('');
      } catch (e) {
        if (!cancelled) {
          setErr(e.message || 'Failed to load quotation');
          setLoadMsg('');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, routeId]);

  const setLine = (idx, key, val) => {
    setForm((f) => {
      const lines = [...f.lines];
      lines[idx] = { ...lines[idx], [key]: val };
      return { ...f, lines };
    });
  };

  const lineDescOptions = () => {
    if (workTypes.length) {
      return workTypes.map((w) => ({ id: String(w.id), label: w.name }));
    }
    return invoiceFors.map((o) => ({ id: `__inv__${o.value}`, label: o.value }));
  };

  const selectedDescValue = (line) => {
    if (line.custom_description) return '__custom__';
    if (line.work_type_id) return String(line.work_type_id);
    if (line.description && invoiceFors.some((o) => o.value === line.description)) {
      return `__inv__${line.description}`;
    }
    if (line.description && !workTypes.some((w) => w.name === line.description)) return '__custom__';
    const byName = workTypes.find((w) => w.name === line.description);
    if (byName) return String(byName.id);
    return '';
  };

  const onLineDescSelect = (idx, value) => {
    setForm((f) => {
      const lines = [...f.lines];
      if (value === '__custom__') {
        lines[idx] = applyWorkTypeToLine(lines[idx], '__custom__');
      } else if (value.startsWith('__inv__')) {
        lines[idx] = {
          ...lines[idx],
          work_type_id: '',
          description: value.replace(/^__inv__/, ''),
          custom_description: false,
        };
      } else {
        lines[idx] = applyWorkTypeToLine(lines[idx], value);
      }
      return { ...f, lines };
    });
  };

  const save = async () => {
    if (!form.customer_id || !form.project_id) {
      setErr('Select both Customer and Project before saving.');
      return;
    }
    if (form.lines.some((l) => !String(l.description || '').trim())) {
      setErr('Each line needs a description (pick from master or Custom).');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const body = {
        customer_id: Number(form.customer_id) || null,
        project_id: Number(form.project_id) || null,
        remarks: form.remarks,
        valid_until: form.valid_until || null,
        lines: form.lines.map((l) => ({
          description: l.description,
          work_type_id: l.work_type_id ? Number(l.work_type_id) : null,
          qty: Number(l.qty),
          uom: l.uom || null,
          rate: Number(l.rate),
          discount_pct: Number(l.discount_pct || 0),
          cgst_pct: Number(l.cgst_pct || 0),
          sgst_pct: Number(l.sgst_pct || 0),
          igst_pct: Number(l.igst_pct || 0),
        })),
      };
      const res = isEdit
        ? await quoteService.update(routeId, body)
        : await quoteService.create(body);
      const id = res.data?.id || routeId;
      navigate(`/quotations/${id}`);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 980 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>{isEdit ? 'Edit Quotation' : 'New Quotation'}</h2>
        <button type="button" onClick={loadMasters} disabled={busy}>
          Reload masters
        </button>
      </div>
      {loadMsg && <p style={{ color: '#64748b' }}>{loadMsg}</p>}
      {err && <p style={{ color: '#b91c1c' }}>{err}</p>}
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr' }}>
        <label>
          Customer ({customers.length})
          <select
            value={form.customer_id}
            onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
            style={{ display: 'block', width: '100%', padding: 8 }}
          >
            <option value="">Select</option>
            {customers.map((c) => (
              <option key={c.id ?? c.customer_id} value={c.id ?? c.customer_id}>
                {c.name || c.customer_name || c.customerName || `Customer #${c.id}`}
              </option>
            ))}
          </select>
        </label>
        <label>
          Project ({projects.length})
          <select
            value={form.project_id}
            onChange={(e) => setForm({ ...form, project_id: e.target.value })}
            style={{ display: 'block', width: '100%', padding: 8 }}
          >
            <option value="">Select</option>
            {projects.map((p) => (
              <option key={p.projectId || p.id} value={p.projectId || p.id}>
                {p.projectName || p.name || `Project #${p.projectId || p.id}`}
              </option>
            ))}
          </select>
        </label>
        <label>
          Valid until
          <input
            type="date"
            value={form.valid_until}
            onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
            style={{ display: 'block', width: '100%', padding: 8 }}
          />
        </label>
        <label>
          Remarks
          <input
            value={form.remarks}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            style={{ display: 'block', width: '100%', padding: 8 }}
          />
        </label>
      </div>

      <h3 style={{ marginTop: 24 }}>
        Lines{' '}
        <span style={{ fontWeight: 400, color: '#64748b', fontSize: 14 }}>
          (Work Types / common master — {workTypes.length || invoiceFors.length} options, UOM from common_master)
        </span>
      </h3>
      {form.lines.map((l, idx) => (
        <div
          key={idx}
          style={{
            display: 'grid',
            gridTemplateColumns: '2.2fr 1fr 0.8fr 1fr 0.8fr 0.8fr',
            gap: 8,
            marginBottom: 10,
            alignItems: 'end',
          }}
        >
          <label style={{ display: 'block' }}>
            Description
            <select
              value={selectedDescValue(l)}
              onChange={(e) => onLineDescSelect(idx, e.target.value)}
              style={{ display: 'block', width: '100%', padding: 8 }}
            >
              <option value="">Select line item</option>
              {lineDescOptions().map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
              <option value="__custom__">Custom…</option>
            </select>
            {(l.custom_description || selectedDescValue(l) === '__custom__') && (
              <input
                placeholder="Custom description"
                value={l.description}
                onChange={(e) => setLine(idx, 'description', e.target.value)}
                style={{ display: 'block', width: '100%', padding: 8, marginTop: 6 }}
              />
            )}
          </label>
          <label style={{ display: 'block' }}>
            UOM
            <select
              value={l.uom || ''}
              onChange={(e) => setLine(idx, 'uom', e.target.value)}
              style={{ display: 'block', width: '100%', padding: 8 }}
            >
              <option value="">—</option>
              {units.map((u) => (
                <option key={u.id} value={u.value}>
                  {u.value}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'block' }}>
            Qty
            <input
              type="number"
              value={l.qty}
              onChange={(e) => setLine(idx, 'qty', e.target.value)}
              style={{ display: 'block', width: '100%', padding: 8 }}
            />
          </label>
          <label style={{ display: 'block' }}>
            Rate
            <input
              type="number"
              value={l.rate}
              onChange={(e) => setLine(idx, 'rate', e.target.value)}
              style={{ display: 'block', width: '100%', padding: 8 }}
            />
          </label>
          <label style={{ display: 'block' }}>
            CGST %
            <input
              type="number"
              value={l.cgst_pct}
              onChange={(e) => setLine(idx, 'cgst_pct', e.target.value)}
              style={{ display: 'block', width: '100%', padding: 8 }}
            />
          </label>
          <label style={{ display: 'block' }}>
            SGST %
            <input
              type="number"
              value={l.sgst_pct}
              onChange={(e) => setLine(idx, 'sgst_pct', e.target.value)}
              style={{ display: 'block', width: '100%', padding: 8 }}
            />
          </label>
        </div>
      ))}
      <button type="button" onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, blankLine()] }))}>
        + Add line
      </button>

      <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
        <button
          disabled={busy}
          onClick={save}
          style={{ background: '#2563eb', color: '#fff', border: 0, padding: '10px 16px', borderRadius: 8 }}
        >
          {isEdit ? 'Save changes' : 'Save DRAFT'}
        </button>
        <button type="button" onClick={() => navigate(isEdit ? `/quotations/${routeId}` : '/quotations')}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function QuotationView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await quoteService.get(id);
      setData(res.data);
    } catch (e) {
      setErr(e.message);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const act = async (fn) => {
    setBusy(true);
    setErr('');
    try {
      await fn();
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!data && !err) return <div style={{ padding: 24 }}>Loading…</div>;

  return (
    <div style={{ padding: 24 }}>
      <button type="button" onClick={() => navigate('/quotations')}>
        ← Back
      </button>
      {err && <p style={{ color: '#b91c1c' }}>{err}</p>}
      {data && (
        <>
          <h2>
            {data.quote_no} <small style={{ color: '#666' }}>({data.status})</small>
          </h2>
          <p>
            Customer #{data.customer_id} · Project #{data.project_id} · Total {data.total_amt} {data.currency}
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                <th style={{ padding: 8 }}>#</th>
                <th>Description</th>
                <th>UOM</th>
                <th>Qty</th>
                <th>Rate</th>
                <th>Taxable</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {(data.lines || []).map((l) => (
                <tr key={l.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 8 }}>{l.line_no}</td>
                  <td>{l.description}</td>
                  <td>{l.uom || '—'}</td>
                  <td>{l.qty}</td>
                  <td>{l.rate}</td>
                  <td>{l.taxable_amt}</td>
                  <td>{l.line_total}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {canEditQuote(data.status) && (
              <button
                type="button"
                className="crane-btn"
                onClick={() => navigate(`/quotations/${data.id}/edit`)}
              >
                Edit
              </button>
            )}
            {data.status === 'DRAFT' && (
              <button disabled={busy} onClick={() => act(() => quoteService.submit(data.id))}>
                Submit
              </button>
            )}
            {data.status === 'SUBMITTED' && (
              <>
                <button disabled={busy} onClick={() => act(() => quoteService.approve(data.id))}>
                  Approve
                </button>
                <button disabled={busy} onClick={() => act(() => quoteService.reject(data.id))}>
                  Reject
                </button>
              </>
            )}
            {data.status === 'APPROVED' && (
              <button
                disabled={busy}
                className="crane-btn crane-btn--primary"
                onClick={() => navigate(`/quotations/${data.id}/convert`)}
              >
                Complete WO details & convert →
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
